import React, { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Calendar, Settings, LogOut, User, BarChart3, CreditCard, Monitor } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth.js';

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, signOut } = useAuth();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Eventos', href: '/events', icon: Calendar },
    { name: 'Pantallas', href: '/screens', icon: Monitor },
    { name: 'Reportes', href: '/reports', icon: BarChart3 },
    { name: 'Facturación', href: '/billing', icon: CreditCard },
    { name: 'Configuración', href: '/settings', icon: Settings },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Bloquear scroll del body cuando el sidebar móvil está abierto
  useEffect(() => {
    if (sidebarOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [sidebarOpen]);

  const renderUserAvatar = () => {
    if (user?.photoURL) {
      return (
        <img
          src={user.photoURL}
          alt={user.displayName || 'Usuario'}
          className="h-8 w-8 rounded-full object-cover"
        />
      );
    }
    return (
      <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center">
        <User className="h-5 w-5 text-white" />
      </div>
    );
  };

  const isActiveRoute = (href) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar para desktop */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 bg-white border-r border-gray-200">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b border-gray-200">
            <h1 className="text-xl font-bold text-indigo-600">
              MuchPic Admin
            </h1>
          </div>

          {/* Navegación */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    isActive
                      ? 'text-indigo-600 bg-indigo-50 border-r-2 border-indigo-600'
                      : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'
                  }`}
                >
                  <Icon className={`mr-3 h-5 w-5 ${
                    isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600'
                  }`} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Usuario */}
          <div className="flex-shrink-0 border-t border-gray-200 p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {renderUserAvatar()}
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">
                  {user?.displayName || user?.email || 'Usuario'}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.provider === 'google' ? 'Google' : 'Email'}
                </p>
                <button
                  onClick={handleSignOut}
                  className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
                >
                  <LogOut className="h-3 w-3 mr-1" />
                  Cerrar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal */}
      <div className="md:pl-64">
        {/* Header móvil */}
        <div className="md:hidden bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">
              MuchPic Admin
            </h1>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="text-gray-500 hover:text-gray-700"
            >
              {sidebarOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Sidebar móvil */}
        {sidebarOpen && (
          <div className="md:hidden">
            <div className="fixed inset-0 z-40 flex">
              {/* Overlay */}
              <div className="fixed inset-0 bg-black bg-opacity-40" onClick={() => setSidebarOpen(false)} />
              {/* Drawer */}
              <div className="relative ml-0 h-full w-full max-w-xs bg-white shadow-xl transform transition-transform ease-out duration-200 translate-x-0">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-gray-900">Menú</h2>
                  <button onClick={() => setSidebarOpen(false)} className="text-gray-500 hover:text-gray-700">
                    <X className="h-6 w-6" />
                  </button>
                </div>
                {/* Items */}
                <div className="h-[calc(100%-56px)] overflow-y-auto">
                  <nav className="px-2 py-3 space-y-1">
                    {navigation.map((item) => {
                      const Icon = item.icon;
                      const isActive = isActiveRoute(item.href);
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`group flex items-center px-3 py-2 text-base font-medium rounded-md transition-colors ${
                            isActive
                              ? 'text-indigo-600 bg-indigo-50'
                              : 'text-gray-700 hover:text-indigo-600 hover:bg-indigo-50'
                          }`}
                          onClick={() => setSidebarOpen(false)}
                        >
                          <Icon className={`mr-3 h-5 w-5 ${
                            isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-indigo-600'
                          }`} />
                          {item.name}
                        </Link>
                      );
                    })}
                  </nav>
                  {/* User */}
                  <div className="border-t border-gray-200 p-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {renderUserAvatar()}
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-700">
                          {user?.displayName || user?.email || 'Usuario'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user?.provider === 'google' ? 'Google' : 'Email'}
                        </p>
                        <button
                          onClick={handleSignOut}
                          className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
                        >
                          <LogOut className="h-3 w-3 mr-1" />
                          Cerrar sesión
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Contenido */}
        <main className="flex-1">
          {children}
        </main>
        <Toaster position="top-right" toastOptions={{ duration: 3500 }} />
      </div>
    </div>
  );
};
